/**
 * Onboard Flow Layout
 *
 * Standalone layout for the onboarding lead-magnet flow.
 * No sidebar, no main navigation — just a clean, focused experience.
 * This layout is registered OUTSIDE the _app layout in routes.ts.
 *
 * Auth is NOT enforced here — the entry page (Step 1) is public.
 * Steps 2+ enforce auth in their individual loaders.
 *
 * The loader reads the session's `currentStep` (furthest step reached)
 * so the progress bar can show all completed steps as clickable,
 * even when the user navigates back to an earlier step.
 *
 * @see {@link ./lib/types.ts} for OnboardStep type
 * @see {@link ./components/onboard-progress.tsx} for progress indicator
 * @see {@link ./__tests__/layout.test.ts}
 */

import { useRef } from 'react'
import type { LoaderFunctionArgs } from 'react-router'
import { Outlet, useLoaderData, useLocation } from 'react-router'
import { BnsnLogoColorWithText } from '@bnsn/ui/atoms'
import { OnboardProgress } from './components/onboard-progress'
import type { OnboardStep } from './lib/types'
import { ONBOARD_STEPS } from './lib/constants'
import { getOnboardSession } from './lib/loader-utils'

/**
 * Maps the current pathname to an OnboardStep identifier.
 */
export const getStepFromPath = (pathname: string): OnboardStep => {
  const segment = pathname.split('/').pop() ?? ''
  const stepMap: Record<string, OnboardStep> = {
    welcome: 'welcome',
    report: 'report',
    assessment: 'assessment',
    'intro-video': 'intro-video',
    'business-info': 'business-info',
  }
  return stepMap[segment] ?? 'entry'
}

/**
 * Returns the 1-based step number for a given step ID.
 */
const getStepNumber = (stepId: OnboardStep): number => {
  const config = ONBOARD_STEPS.find((s) => s.id === stepId)
  return config?.step ?? 1
}

/**
 * Layout loader — reads the session to provide the furthest completed step.
 * The session's `currentStep` is advanced by form actions (completing a step),
 * so it represents the furthest step the user has actually finished.
 */
export const loader = async ({ request }: LoaderFunctionArgs) => {
  const session = await getOnboardSession(request)
  return { completedStep: session?.currentStep ?? null }
}

/**
 * Onboard Layout Component
 *
 * Provides a distraction-free, centered layout for the onboard flow.
 * Tracks the highest step visited client-side so the progress bar
 * updates immediately without waiting for cookie propagation.
 */
const OnboardLayout = (): JSX.Element => {
  const location = useLocation()
  const { completedStep } = useLoaderData<typeof loader>()
  const currentStep = getStepFromPath(location.pathname)

  // Client-side high-water mark: tracks the furthest step visited in this browser session.
  const completedStepNumber = completedStep ? getStepNumber(completedStep) : 0
  const currentStepNumber = getStepNumber(currentStep)
  const initialHighWater = Math.max(completedStepNumber, currentStepNumber)
  const highWaterRef = useRef(initialHighWater)

  if (currentStepNumber > highWaterRef.current) {
    highWaterRef.current = currentStepNumber
  }
  if (completedStepNumber > highWaterRef.current) {
    highWaterRef.current = completedStepNumber
  }

  const visitedStep = ONBOARD_STEPS.find((s) => s.step === highWaterRef.current)?.id

  return (
    <div className="min-h-screen flex flex-col bg-[url('/images/onboard-bg.png')] bg-cover bg-center bg-no-repeat">
      {/* Header — BNSN logo, centered */}
      <header className="py-4 px-6 flex justify-center">
        <BnsnLogoColorWithText height={36} />
      </header>

      {/* Main content area */}
      <main className="flex-1 flex flex-col gap-4 p-4 sm:p-8">
        <OnboardProgress currentStep={currentStep} completedStep={completedStep ?? undefined} visitedStep={visitedStep} />
        <Outlet />
      </main>
    </div>
  )
}

export default OnboardLayout
